import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Flex, Stack, Surface } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Dialog,
  EmptyState,
  LoadingState,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Textarea,
  useToast,
  type StatusTone,
} from "@/components/ui";
import { ApiError, apiErrorMessage } from "@/api/client";
import type { BusinessesApi } from "../api";
import type { RequirementDocument } from "../labels";
import { formatLabel, outstandingDocumentLabels } from "../labels";
import type {
  BusinessDocumentAccessDto,
  BusinessDocumentDto,
  BusinessDocumentStatus,
  BusinessStatus,
} from "../types";

/**
 * The applicant's verification documents, for a reviewer — ADR-004.
 *
 * Nothing on this page holds a URL until a reviewer clicks Open. That click
 * asks the API for a two-minute signed link (and the API writes an audit row
 * before it signs), the file is shown in a viewer, and the link is dropped when
 * the viewer closes or the two minutes run out. It is never put in the address
 * bar, in state that outlives the viewer, or anywhere a browser would cache it.
 *
 * A document's verdict here (accepted / rejected) is evidence for the business
 * decision, not the decision itself. Approving, sending back or refusing the
 * business is still the Status action, which is where `review_required_documents`
 * is set — so "Send back for these" hands the rejected keys to that dialog.
 */

const LIVE: BusinessDocumentStatus[] = [
  "requested",
  "submitted",
  "under_review",
  "accepted",
  "rejected",
];

/** Mirrors DOCUMENT_UPLOAD_BUSINESS_STATUSES in the API. */
const APPLICATION_OPEN: BusinessStatus[] = [
  "draft",
  "applied",
  "under_review",
  "more_information_required",
];

/** The API's minimum for a rejection reason the applicant can act on. */
const REJECT_REASON_MIN = 3;

function documentTone(status: BusinessDocumentStatus): StatusTone {
  switch (status) {
    case "accepted":
      return "success";
    case "rejected":
      return "danger";
    case "submitted":
    case "under_review":
    case "requested":
      return "warning";
    default:
      return "neutral";
  }
}

function formatSize(bytes: number | null): string {
  if (bytes === null || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export interface VerificationDocumentsPanelProps {
  api: BusinessesApi;
  businessId: string;
  /**
   * What this business type asks for, from the served requirement list.
   * `null` means the list could not be loaded — outstanding is then unknown,
   * never "none".
   */
  requirements: RequirementDocument[] | null;
  /** Bumped by the page's live refresh so this list stays current too. */
  refreshToken: number;
  /** Where the business stands — decides whether a rejection can be acted on. */
  businessStatus: BusinessStatus;
  /** The applicant's answers, to resolve the conditional DTI/SEC document. */
  answers?: Record<string, unknown> | null;
  /** Rejected requirement keys → the Status dialog, preset to Modify. */
  onSendBack?: (keys: string[]) => void;
  /** False when "request more information" is not a legal action right now. */
  canSendBack?: boolean;
  /**
   * Reports what is still outstanding, for the approve warning. `null` means
   * UNKNOWN — the documents are loading, failed to load, or may not be read —
   * which is never the same as "nothing outstanding".
   */
  onOutstandingChange?: (labels: string[] | null) => void;
}

export function VerificationDocumentsPanel({
  api,
  businessId,
  requirements: requirementsOrNull,
  refreshToken,
  businessStatus,
  answers = null,
  onSendBack,
  canSendBack = false,
  onOutstandingChange,
}: VerificationDocumentsPanelProps) {
  const toast = useToast();
  const requirementsUnknown = requirementsOrNull === null;
  const requirements = useMemo(() => requirementsOrNull ?? [], [requirementsOrNull]);
  const [documents, setDocuments] = useState<BusinessDocumentDto[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);

  const [viewing, setViewing] = useState<BusinessDocumentDto | null>(null);
  const [rejecting, setRejecting] = useState<BusinessDocumentDto | null>(null);
  const [deciding, setDeciding] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  /**
   * Set when the API refuses a decision with 403 — a reviewer with
   * `verification:view` but not `manage`, or one tied to this business. The
   * table then turns read-only instead of offering buttons that always fail.
   */
  const [decisionDenied, setDecisionDenied] = useState<string | null>(null);
  const applicationOpen = APPLICATION_OPEN.includes(businessStatus);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const res = await api.listDocuments(businessId);
        setDocuments(res.documents);
        setError(null);
        setDenied(false);
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          setDenied(true);
        } else {
          setError(apiErrorMessage(err));
        }
        // A failed background refresh keeps what was on screen.
        if (!silent) setDocuments(null);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [api, businessId],
  );

  useEffect(() => {
    void load(refreshToken > 0);
  }, [load, refreshToken]);

  const live = useMemo(
    () => (documents ?? []).filter((d) => LIVE.includes(d.status)),
    [documents],
  );
  const history = useMemo(
    () => (documents ?? []).filter((d) => !LIVE.includes(d.status)),
    [documents],
  );
  const byKey = useMemo(() => {
    const map = new Map<string, BusinessDocumentDto>();
    for (const d of live) if (!map.has(d.requirement_key)) map.set(d.requirement_key, d);
    return map;
  }, [live]);

  const labelFor = useCallback(
    (key: string) => requirements.find((r) => r.key === key)?.label ?? formatLabel(key),
    [requirements],
  );

  // Every requirement row, plus any uploaded key the list no longer names.
  const rows = useMemo(() => {
    const keys = requirements.map((r) => r.key);
    for (const d of live) if (!keys.includes(d.requirement_key)) keys.push(d.requirement_key);
    return keys;
  }, [requirements, live]);

  const rejectedKeys = useMemo(
    () => live.filter((d) => d.status === "rejected").map((d) => d.requirement_key),
    [live],
  );

  useEffect(() => {
    if (!onOutstandingChange) return;
    if (documents === null || requirementsUnknown) {
      onOutstandingChange(null);
      return;
    }
    const accepted = new Set(
      [...byKey.values()].filter((d) => d.status === "accepted").map((d) => d.requirement_key),
    );
    onOutstandingChange(outstandingDocumentLabels(requirements, accepted, answers));
  }, [onOutstandingChange, requirements, requirementsUnknown, byKey, documents, answers]);

  /** The document that took an old one's place: the next upload for its key. */
  const replacementFor = useCallback(
    (old: BusinessDocumentDto): BusinessDocumentDto | null => {
      const later = (documents ?? [])
        .filter(
          (d) =>
            d.id !== old.id &&
            d.requirement_key === old.requirement_key &&
            (d.submitted_at ?? "") > (old.submitted_at ?? ""),
        )
        .sort((a, b) => (a.submitted_at ?? "").localeCompare(b.submitted_at ?? ""));
      return later[0] ?? null;
    },
    [documents],
  );

  const decide = async (doc: BusinessDocumentDto, decision: "accept" | "reject", reason?: string) => {
    setDeciding(doc.id);
    try {
      await api.decideDocument(businessId, doc.id, decision, reason);
      toast.push({
        title: decision === "accept" ? "Document accepted" : "Document rejected",
        description: labelFor(doc.requirement_key),
        tone: decision === "accept" ? "success" : "warning",
      });
      setRejecting(null);
      await load(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setDecisionDenied(apiErrorMessage(err));
        setRejecting(null);
      }
      toast.push({ title: "Decision not saved", description: apiErrorMessage(err), tone: "danger" });
    } finally {
      setDeciding(null);
    }
  };

  let body: ReactNode;
  if (loading && documents === null) {
    body = <LoadingState message="Loading documents…" />;
  } else if (denied) {
    body = (
      <EmptyState
        title="You cannot view verification documents"
        description="Reading a merchant's permits and IDs needs the verification view permission. Ask an administrator to grant it to your access assignment."
        reason="scope"
      />
    );
  } else if (error && documents === null) {
    body = (
      <EmptyState
        title="Documents unavailable"
        description={error}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  } else if (rows.length === 0 && !requirementsUnknown) {
    body = (
      <EmptyState
        title="No document list for this type"
        description="This business type has no requirement list, so the applicant is not asked for documents."
        reason="none"
      />
    );
  } else {
    body = (
      <Stack gap={12}>
        {requirementsUnknown ? (
          <Alert tone="warning" title="Documents could not be checked">
            The document list for this business type did not load, so what is
            still owed is unknown. Reload the page before approving.
          </Alert>
        ) : null}
        {live.length === 0 ? (
          <Alert tone="info" title="Nothing uploaded yet">
            The applicant uploads these from the HUDDL app, on their application
            screen. They appear here as soon as each one finishes.
          </Alert>
        ) : null}
        {decisionDenied ? (
          <Alert tone="info" title="Read-only for you">
            {decisionDenied} You can still open documents and see each status;
            accepting or rejecting needs another reviewer.
          </Alert>
        ) : null}
        {!applicationOpen && live.length > 0 ? (
          <Alert tone="info" title={`Application is ${formatLabel(businessStatus).toLowerCase()}`}>
            The applicant can no longer replace documents, so rejecting one is
            closed. Accepting still records that a document was checked.
          </Alert>
        ) : null}
        {rejectedKeys.length > 0 && !canSendBack && applicationOpen ? (
          <Alert tone="info" title="Sending back is not available right now">
            An application can be sent back only while it is applied or under
            review; this one is {formatLabel(businessStatus).toLowerCase()}. The
            applicant still sees each rejection and its reason.
          </Alert>
        ) : null}
        {rejectedKeys.length > 0 && onSendBack && canSendBack ? (
          <Alert tone="warning" title="Rejected documents are not sent back automatically">
            <Flex gap={8} align="center" wrap>
              <Text role="small" as="span">
                The applicant sees each reason now. To hand the application back
                for replacements, send it back with these ticked.
              </Text>
              <Button size="sm" variant="secondary" onClick={() => onSendBack(rejectedKeys)}>
                Send back for {rejectedKeys.length} document{rejectedKeys.length === 1 ? "" : "s"}
              </Button>
            </Flex>
          </Alert>
        ) : null}
        <Table>
          <Thead>
            <Tr>
              <Th>Document</Th>
              <Th>Status</Th>
              <Th>File</Th>
              <Th>Submitted</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((key) => {
              const req = requirements.find((r) => r.key === key);
              const doc = byKey.get(key);
              return (
                <Tr key={key}>
                  <Td>
                    <Stack gap={4}>
                      <Text role="body" as="span">{labelFor(key)}</Text>
                      <Text role="caption" color="muted" as="span">
                        {req?.conditional
                          ? "If it applies"
                          : req?.required
                            ? "Required"
                            : req
                              ? "Optional"
                              : "No longer on the list"}
                      </Text>
                    </Stack>
                  </Td>
                  <Td>
                    {doc ? (
                      <Stack gap={4}>
                        <StatusChip tone={documentTone(doc.status)}>
                          {formatLabel(doc.status)}
                        </StatusChip>
                        {doc.status === "rejected" && doc.decision_reason ? (
                          <Text role="caption" color="muted" as="span">
                            {doc.decision_reason}
                          </Text>
                        ) : null}
                      </Stack>
                    ) : (
                      <Text role="small" color="muted" as="span">Not uploaded</Text>
                    )}
                  </Td>
                  <Td>
                    {doc ? (
                      <Text role="small" as="span">
                        {doc.file_name ?? "—"} · {formatSize(doc.size_bytes)}
                      </Text>
                    ) : "—"}
                  </Td>
                  <Td>{doc ? formatWhen(doc.submitted_at) : "—"}</Td>
                  <Td>
                    {doc ? (
                      <Flex gap={4} wrap>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!doc.file_available}
                          onClick={() => setViewing(doc)}
                        >
                          Open
                        </Button>
                        {doc.status !== "accepted" && !decisionDenied ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={deciding === doc.id}
                            // Nothing to rule on without a readable file.
                            disabled={deciding !== null || !doc.file_available}
                            title={doc.file_available ? undefined : "No readable file to review"}
                            onClick={() => void decide(doc, "accept")}
                          >
                            Accept
                          </Button>
                        ) : null}
                        {doc.status !== "rejected" && !decisionDenied && applicationOpen ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={deciding !== null || !doc.file_available}
                            title={doc.file_available ? undefined : "No readable file to review"}
                            onClick={() => setRejecting(doc)}
                          >
                            Reject
                          </Button>
                        ) : null}
                      </Flex>
                    ) : null}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>

        {history.length > 0 ? (
          <Stack gap={8}>
            <Button size="sm" variant="ghost" onClick={() => setShowHistory((v) => !v)}>
              {showHistory ? "Hide" : "Show"} {history.length} replaced document{history.length === 1 ? "" : "s"}
            </Button>
            {showHistory ? (
              <Table>
                <Thead>
                  <Tr>
                    <Th>Document</Th>
                    <Th>Status</Th>
                    <Th>File</Th>
                    <Th>Submitted</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {history.map((doc) => {
                    const replacement = replacementFor(doc);
                    return (
                      <Tr key={doc.id}>
                        <Td>{labelFor(doc.requirement_key)}</Td>
                        <Td>
                          <Stack gap={4}>
                            <StatusChip tone="neutral">{formatLabel(doc.status)}</StatusChip>
                            {/* A reason on a replaced row is always a rejection's:
                                accepting stores no reason on the document. */}
                            {doc.decision_reason ? (
                              <Text role="caption" color="muted" as="span">
                                Rejected earlier: {doc.decision_reason}
                              </Text>
                            ) : null}
                          </Stack>
                        </Td>
                        <Td>{doc.file_name ?? "—"} · {formatSize(doc.size_bytes)}</Td>
                        <Td>
                          <Stack gap={4}>
                            <span>{formatWhen(doc.submitted_at)}</span>
                            {replacement ? (
                              <Text role="caption" color="muted" as="span">
                                Replaced by {replacement.file_name ?? "a newer upload"} ·{" "}
                                {formatWhen(replacement.submitted_at)}
                              </Text>
                            ) : null}
                          </Stack>
                        </Td>
                        <Td>
                          <Flex gap={4} wrap>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!doc.file_available}
                              onClick={() => setViewing(doc)}
                            >
                              Open
                            </Button>
                            {replacement ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={!replacement.file_available}
                                onClick={() => setViewing(replacement)}
                              >
                                Open replacement
                              </Button>
                            ) : null}
                          </Flex>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    );
  }

  return (
    <Surface>
      <Stack gap={16}>
        <Flex justify="space-between" align="center" wrap gap={8}>
          <Stack gap={4}>
            <Text role="h2" as="h2">
              Verification documents
            </Text>
            <Text role="small" color="secondary" as="p">
              Private. Each Open is logged and issues a link that expires in two minutes.
            </Text>
          </Stack>
          <Button size="sm" variant="secondary" onClick={() => void load()}>
            Refresh
          </Button>
        </Flex>
        {body}
      </Stack>

      <DocumentViewer
        api={api}
        businessId={businessId}
        document={viewing}
        title={viewing ? labelFor(viewing.requirement_key) : ""}
        onClose={() => setViewing(null)}
      />

      <RejectDocumentDialog
        document={rejecting}
        title={rejecting ? labelFor(rejecting.requirement_key) : ""}
        busy={rejecting !== null && deciding === rejecting.id}
        onClose={() => setRejecting(null)}
        onConfirm={(reason) => rejecting && void decide(rejecting, "reject", reason)}
      />
    </Surface>
  );
}

/* ── Viewer ───────────────────────────────────────────────────────────── */

function DocumentViewer({
  api,
  businessId,
  document,
  title,
  onClose,
}: {
  api: BusinessesApi;
  businessId: string;
  document: BusinessDocumentDto | null;
  title: string;
  onClose: () => void;
}) {
  const [access, setAccess] = useState<BusinessDocumentAccessDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(0);

  const fetchAccess = useCallback(async () => {
    if (!document) return;
    setLoading(true);
    setError(null);
    setAccess(null);
    try {
      const res = await api.openDocument(businessId, document.id);
      setAccess(res.access);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? "You do not have permission to open verification documents."
          : apiErrorMessage(err),
      );
    } finally {
      setLoading(false);
    }
  }, [api, businessId, document]);

  // One link per opening. Closing drops it; reopening mints a new one.
  useEffect(() => {
    if (document) void fetchAccess();
    else {
      setAccess(null);
      setError(null);
    }
  }, [document, fetchAccess]);

  // The link dies server-side at expires_at; stop showing it at the same time.
  useEffect(() => {
    if (!access) return;
    const until = Date.parse(access.expires_at);
    const tick = () => {
      const left = Math.max(0, Math.round((until - Date.now()) / 1000));
      setRemaining(left);
      if (left === 0) setAccess(null);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [access]);

  const isImage = access?.media_type?.startsWith("image/") ?? false;
  const isPdf = access?.media_type === "application/pdf";

  return (
    <Dialog open={document !== null} onClose={onClose} title={title || "Document"}>
      <Stack gap={12}>
        {loading ? <LoadingState message="Requesting a secure link…" /> : null}
        {error ? (
          <Alert tone="danger" title="Could not open the document">
            <Stack gap={8}>
              <Text role="small" as="p">{error}</Text>
              <Button size="sm" variant="secondary" onClick={() => void fetchAccess()}>
                Try again
              </Button>
            </Stack>
          </Alert>
        ) : null}
        {!loading && !error && !access && document ? (
          <Alert tone="info" title="Link expired">
            <Stack gap={8}>
              <Text role="small" as="p">
                Secure links last two minutes. Opening again issues a new one and is logged.
              </Text>
              <Button size="sm" variant="secondary" onClick={() => void fetchAccess()}>
                Open again
              </Button>
            </Stack>
          </Alert>
        ) : null}
        {access ? (
          <>
            <Flex justify="space-between" align="center" wrap gap={8}>
              <Text role="caption" color="muted" as="span">
                {access.file_name ?? "Document"} · link expires in {remaining}s
              </Text>
              <a href={access.url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
                Open in a new tab
              </a>
            </Flex>
            {isImage ? (
              <img
                src={access.url}
                alt={title}
                referrerPolicy="no-referrer"
                style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
              />
            ) : isPdf ? (
              <iframe
                src={access.url}
                title={title}
                referrerPolicy="no-referrer"
                style={{ width: "100%", height: "70vh", border: 0 }}
              />
            ) : (
              <Text role="small" as="p">Use “Open in a new tab” to view this file.</Text>
            )}
          </>
        ) : null}
      </Stack>
    </Dialog>
  );
}

/* ── Reject ───────────────────────────────────────────────────────────── */

function RejectDocumentDialog({
  document,
  title,
  busy,
  onClose,
  onConfirm,
}: {
  document: BusinessDocumentDto | null;
  title: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (document) setReason("");
  }, [document]);
  const length = reason.trim().length;
  const ok = length >= REJECT_REASON_MIN;
  return (
    <Dialog
      open={document !== null}
      onClose={onClose}
      title={`Reject ${title}`}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" loading={busy} disabled={!ok} onClick={() => onConfirm(reason.trim())}>
            Reject document
          </Button>
        </>
      }
    >
      <Stack gap={12}>
        <Text role="small" color="secondary" as="p">
          The applicant reads this reason in the app next to the document. Say
          what is wrong so they can replace it — unreadable, expired, wrong name.
        </Text>
        <Textarea
          label="Reason"
          value={reason}
          maxLength={500}
          onChange={(event) => setReason(event.target.value)}
        />
        <Text role="caption" color="muted" as="p">
          {ok
            ? `${length}/500`
            : `At least ${REJECT_REASON_MIN} characters, so the applicant knows what to fix${
                length > 0 ? ` (${REJECT_REASON_MIN - length} more)` : ""
              }.`}
        </Text>
      </Stack>
    </Dialog>
  );
}
